import { Typography } from "antd";
import React from "react";
import { useCallHistoryProvider } from "../context/CallHIstoryContext";
import { useTheme } from "../../../ThemeContext";

const { Text } = Typography;

export const Transcripts = ({ searchInput }) => {
  const { callContent } = useCallHistoryProvider();
  const { currentTheme, themeMode } = useTheme();
  
  // Support both old and new API formats
  const transcriptionData = callContent?.transcription || callContent?.call_transcription_fragment || [];
  const transcriptionVerified = callContent?.transcription_verified || callContent?.isReviewed;

  // Function to format time from ISO string to HH:MM:SS
  const formatTime = (timeString) => {
    if (!timeString || timeString === "null") return "0:00";
    
    try {
      const date = new Date(timeString);
      return date.toLocaleTimeString('en-US', { 
        hour12: false, 
        hour: '2-digit', 
        minute: '2-digit'
      });
    } catch (error) {
      console.warn("Error parsing time:", timeString, error);
      return "0:00";
    }
  };

  // Function to get speaker display name and color
  const getSpeakerInfo = (speaker) => {
    const speakerName = speaker || 'Unknown';
    const formattedName = speakerName.charAt(0).toUpperCase() + speakerName.slice(1).toLowerCase();
    
    // Check if speaker is Joy (AI assistant) and apply signature orange color
    const isJoy = speakerName.toLowerCase() === 'joy';
    
    return {
      name: formattedName,
      color: isJoy ? currentTheme.callhistory.orange : currentTheme.callhistory.text,
      isJoy: isJoy
    };
  };

  // Generate stable unique key for each item
  const getStableKey = (item, index) => {
    if (item._id) return `transcript-${item._id}`;
    if (item.utterance_id) return `transcript-${item.utterance_id}`;
    // Create a stable key based on content and position
    const contentHash = (item.utterance || item.text || item.rawUtterance || '').slice(0, 20);
    const timeHash = item.startTime || item.time_start || '';
    return `transcript-${index}-${contentHash.replace(/\s+/g, '')}-${timeHash}`;
  };

  // Filter messages based on search input - moved before speakerStats calculation
  const filteredTranscription = React.useMemo(() => {
    if (!transcriptionData || transcriptionData.length === 0) {
      return [];
    }
    
    if (!searchInput || searchInput.trim() === '') {
      return transcriptionData;
    }
    
    const searchTerm = searchInput.toLowerCase().trim();
    
    try {
      const filtered = transcriptionData.filter((item) => {
        const text = item.utterance || item.text || item.rawUtterance || '';
        const speaker = item.speaker || item.telecom_persona_type || '';
        
        const textMatch = text.toLowerCase().includes(searchTerm);
        const speakerMatch = speaker.toLowerCase().includes(searchTerm);
        
        return textMatch || speakerMatch;
      });
      
      return filtered;
    } catch (error) {
      console.error('Error filtering transcription:', error);
      return transcriptionData; // Return original data if filtering fails
    }
  }, [transcriptionData, searchInput]);

  // Calculate speaker statistics
  const speakerStats = React.useMemo(() => {
    if (!filteredTranscription || filteredTranscription.length === 0) return '';
    
    try {
      const speakerCounts = {};
      filteredTranscription.forEach(item => {
        const speaker = item.speaker || item.telecom_persona_type || 'Unknown';
        const speakerName = speaker.charAt(0).toUpperCase() + speaker.slice(1).toLowerCase();
        speakerCounts[speakerName] = (speakerCounts[speakerName] || 0) + 1;
      });
      
      const total = filteredTranscription.length;
      const speakerPercentages = Object.entries(speakerCounts)
        .map(([speaker, count]) => `${speaker} (${Math.round((count / total) * 100)}%)`)
        .join(', ');
      
      return speakerPercentages;
    } catch (error) {
      console.error('Error calculating speaker stats:', error);
      return '';
    }
  }, [filteredTranscription]);

  const renderMessage = React.useCallback((item, index) => {
    // Support both old and new API formats
    const speaker = item.speaker || item.telecom_persona_type || 'Unknown';
    const startTime = item.startTime || item.time_start;
    const text = item.utterance || item.text || item.rawUtterance || '';
    
    const speakerInfo = getSpeakerInfo(speaker);
    const formattedTime = formatTime(startTime);
    const stableKey = getStableKey(item, index);
    
    return (
      <div 
        key={stableKey}
        style={{
          marginBottom: '16px',
          paddingBottom: '12px',
          borderBottom: index < filteredTranscription.length - 1 ? `1px solid ${currentTheme.callhistory.borderColor}` : 'none'
        }}
      >
        {/* Speaker and Time Header */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          marginBottom: '8px',
          gap: '8px'
        }}>
          <Text style={{ 
            color: speakerInfo.color,
            fontWeight: '600',
            fontSize: '14px'
          }}>
            {speakerInfo.name}
          </Text>
          <Text style={{ 
            color: currentTheme.callhistory.textLight,
            fontSize: '12px'
          }}>
            {formattedTime}
          </Text>
        </div>

        {/* Message Text */}
        <div style={{
          paddingLeft: '0px'
        }}>
          <Text style={{ 
            color: currentTheme.callhistory.text,
            fontSize: '14px',
            lineHeight: '1.5',
            fontWeight: '400'
          }}>
            {text}
          </Text>
        </div>
      </div>
    );
  }, [filteredTranscription.length, currentTheme]);

  return (
    <div style={{ 
      width: "100%", 
      height: "100%", // FIXED CONTAINER SIZE - Use full height from parent container
      display: "flex",
      flexDirection: "column"
    }}>
      <hr style={{ 
        border: 'none', 
        borderTop: `1px solid ${currentTheme.callhistory.borderColor}`,
        margin: '1px 0',
        flexShrink: 0 // FIXED CONTAINER SIZE - Don't shrink the divider
      }} />
      
      {/* Header with Title and Speakers Summary */}
      <div style={{ 
        display: "flex", 
        alignItems: "center", 
        justifyContent: "space-between",
        marginBottom: '4px',
        flexShrink: 0 // FIXED CONTAINER SIZE - Don't shrink the header
      }}>
        <Text style={{
          color: currentTheme.callhistory.text,
          fontSize: "18px",
          fontWeight: '600'
        }}>
          Call Transcript
        </Text>
        
        <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
          {/* Speakers Summary */}
          {filteredTranscription && filteredTranscription.length > 0 && speakerStats && (
            <div style={{
              padding: '8px 12px',
              backgroundColor: themeMode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.02)',
              borderRadius: '8px',
              border: `1px solid ${currentTheme.callhistory.borderColor}`
            }}>
              <Text style={{
                color: currentTheme.callhistory.text,
                fontSize: '12px',
                fontWeight: '600',
                marginRight: '8px'
              }}>
                Speakers:
              </Text>
              <Text style={{
                color: currentTheme.callhistory.textLight,
                fontSize: '12px'
              }}>
                {speakerStats}
              </Text>
            </div>
          )}
          
          {/* Verification Badge */}
          {transcriptionVerified && (
            <div style={{
              display: "flex",
              alignItems: "center",
              gap: '8px',
              backgroundColor: themeMode === 'dark' ? 'rgba(76, 175, 80, 0.2)' : 'rgba(76, 175, 80, 0.1)',
              padding: '4px 12px',
              borderRadius: '16px',
              border: '1px solid rgba(76, 175, 80, 0.3)'
            }}>
              <img
                src="/icons/verified.svg"
                width="16"
                height="16"
                alt="Verified"
              />
              <Text style={{ 
                color: '#4CAF50',
                fontSize: '12px',
                fontWeight: '500'
              }}>
                Human Verified
              </Text>
            </div>
          )}
        </div>
      </div>

      {/* Transcript Messages */}
      <div
        style={{ 
          flex: 1, // FIXED CONTAINER SIZE - Take remaining space in flex container
          backgroundColor: currentTheme.callhistory.mainBackground,
          borderRadius: '8px',
          padding: '16px',
          overflowY: 'auto', // FIXED CONTAINER SIZE - Show scrollbar when content exceeds height
          overflowX: 'hidden',
          minHeight: 0, // Allow flex child to shrink below content size
          maxHeight: '100%', // FIXED CONTAINER SIZE - Ensure container doesn't exceed parent height
          // Custom scrollbar styling - visible scrollbar when needed
          scrollbarWidth: 'thin',
          scrollbarColor: `${currentTheme.callhistory.borderColor} ${currentTheme.callhistory.mainBackground}`,
        }}
        className={`transcript-scroll ${themeMode === "light" ? "light-scroll" : "dark-scroll"}`}
      >
        <style jsx>{`
          .transcript-scroll::-webkit-scrollbar {
            width: 8px;
            background: ${currentTheme.callhistory.mainBackground};
          }
          .transcript-scroll::-webkit-scrollbar-track {
            background: ${currentTheme.callhistory.mainBackground};
            border-radius: 4px;
            border: 1px solid ${currentTheme.callhistory.borderColor}20;
          }
          .transcript-scroll::-webkit-scrollbar-thumb {
            background-color: ${currentTheme.callhistory.borderColor};
            border-radius: 4px;
            border: none;
          }
          .transcript-scroll::-webkit-scrollbar-thumb:hover {
            background-color: ${themeMode === 'dark' ? 'rgba(255, 255, 255, 0.5)' : 'rgba(0, 0, 0, 0.5)'};
          }
          .transcript-scroll::-webkit-scrollbar-corner {
            background: ${currentTheme.callhistory.mainBackground};
          }
        `}</style>
        
        <div style={{ 
          display: 'flex', 
          flexDirection: 'column'
        }}>
          {filteredTranscription && filteredTranscription.length > 0 ? (
            filteredTranscription.map((item, index) => renderMessage(item, index))
          ) : (
            <div 
              key="empty-state-message"
              style={{
                textAlign: 'center',
                padding: '40px 20px',
                color: currentTheme.callhistory.textLight
              }}
            >
              <Text style={{ color: currentTheme.callhistory.textLight }}>
                {searchInput && searchInput.trim() !== '' 
                  ? 'No messages found matching your search.' 
                  : 'No transcript available.'}
              </Text>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
